import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormControl, Tab, Grid, Table, CommonErrors } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { Page, Group, Layout, Button, ExcelUpload } from "@/comn/components";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";
import {
    useForm,
    useFetch,
    useGrid,
    useResource,
    usePopup,
    useStore,
    useToast,
    useAuth,
    useModal,
    useTab,
    usePage,
} from "@/comn/hooks";
import { BASE, URLS, APIS, SF_HS_CTGR_ITM_U } from "@/tz/go/egaz/osci/lpco/hscd/ctgr/service/OsciHscdCtgrService";

export const OSCI0204008S = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0004"],
    });
    const pgeTitle = "Registering Control HS Code"; // Page Title !== 페이지 타이틀 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!

    const { hsCtgrId } = useParams();

    const refOrinData = useRef();

    /*
     * Code Resource
     * !== 사용할 Code 정의 ==!
     */
    useResource({
        defaultSchema: [{ area: "hsCd" }, { area: "comnCd", comnCd: "OSC0130" }],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Registering HS Code Form !== HS 신청 Form==!
        hsCdForm: useForm({
            defaultSchema: SF_HS_CTGR_ITM_U,
            defaultValues: {
                orgCd: auth.get("orgCd"),
                orgNm: auth.get("orgNm"),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {};
    const render = {};

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getHsCtgrItmInfo: useFetch({
            api: (hsCtgrId: any) => {
                return APIS.getHsCtgrItmInfo(hsCtgrId);
            },
            onSuccess: (data) => {
                form.hsCdForm.setValues(data.info?.content);

                refOrinData.current = data.info?.content;
            },
            onError: () => {},
            showToast: false,
        }),

        saveHsCtgrItm: useFetch({
            api: (hsCtgrId, data) => APIS.saveHsCtgrItm(hsCtgrId, { ...data }),
            onSuccess: (data) => {
                if (data.saveInfo?.content?.errorList?.length > 0) {
                    modal.openModal({
                        type: "messages",
                        data: data.saveInfo?.content?.errorList,
                    });
                } else {
                    modal.openModal({
                        content: "msg.osc.00006" /**Processed successfully. */,
                        onConfirm: () => {
                            navigate(URLS.osci0204007s + "/" + hsCtgrId);
                        },
                        onCancel: () => {
                            navigate(URLS.osci0204008s + "/" + hsCtgrId);
                        },
                    });
                }
            },
            onError: (error) => {
                console.log(error.response?.data?.errorMessage);
                const msg = error.response?.data?.errorMessage || "msg.osc.00015";
                modal.openModal({
                    content: msg,
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Save Component !== Additional Info 저장 ==!
        saveHsCtgrItm: form.hsCdForm.handleSubmit(async () => {
            // hs code duplication check
            let arrValdn: any[] = [];
            const errResult: any = await APIS.getHsCtgrItmValdn({
                hsCd: form.hsCdForm.getValue("hsCd"),
                hsCtgrId: hsCtgrId,
                mode: "HS",
            });
            const backValdn = errResult.data?.result?.content || [];
            if (backValdn.length > 0) {
                arrValdn = arrValdn.concat(backValdn);
            }
            const appdStrtDt = form.hsCdForm.getValue("appdStrtDt");
            const appdEndDt = form.hsCdForm.getValue("appdEndDt");

            if (appdStrtDt && appdEndDt) {
                // 비교
                if (comnUtils.compareDate(appdStrtDt, appdEndDt) == -1) {
                    arrValdn.push(lpcoUtils.getErrorMessagesObj(t("L_VLD_STRT_DT"), t("msg.osc.00057")));
                    arrValdn.push(lpcoUtils.getErrorMessagesObj(t("L_VLD_END_DT"), t("msg.osc.00057")));
                }
            }
            if (arrValdn.length > 0) {
                // error layer popup
                modal.openModal({
                    content: <CommonErrors errors={[...arrValdn]} type="messages" />,
                    size: "lg",
                    draggable: true,
                });
            } else {
                const data = { ...form.hsCdForm.getValues() };
                modal.openModal({
                    content: "msg.osc.00090" /**Do you want to save ? */,
                    onConfirm: () => {
                        fetch.saveHsCtgrItm.fetch(hsCtgrId, data);
                    },
                });
            }
        }),

        setInit: async () => {
            // OGA HS CODE LIST
            await fetch.getHsCtgrItmInfo.fetch(hsCtgrId);
        },
    };

    // useEffect
    useEffect(() => {
        handler.setInit();
    }, []);

    return (
        <Page>
            {/* <form> */}
            <Group>
                <Group.Body>
                    <Layout>
                        <Layout.Left>
                            <Group.Title title={t("T_ITM_CTGR")} titleSize={1}></Group.Title>
                        </Layout.Left>
                    </Layout>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.hsCdForm.schema.itmCtgrTpCd} />
                            <Group.Control {...form.hsCdForm.schema.hsCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.hsCdForm.schema.rmrk} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.hsCdForm.schema.appdStrtDt} />
                            <Group.Control {...form.hsCdForm.schema.appdEndDt} />
                        </Group.Row>
                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="list"
                                onClick={() => {
                                    navigate(URLS.osci0204006q);
                                }}
                            />
                            <Button
                                role="reset"
                                onClick={() => {
                                    form.hsCdForm.reset(refOrinData.current);
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role="save"
                                onClick={() => {
                                    handler.saveHsCtgrItm();
                                }}
                            />
                            <Button
                                role="cancel"
                                onClick={() => {
                                    navigate(URLS.osci0204007s + "/" + hsCtgrId);
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
        </Page>
    );
};
